import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, CheckCircle2, Circle, Coins, Gift, Handshake, MapPin, Monitor, Target, Users, UserCheck } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { api } from "../../lib/api";
import { MODE_LABELS, formatEventDate, money, shortDate, venueLine } from "../../lib/format";
import { DataTable, InfoTip, PageHeader, StatusPill, TestBadge } from "../../components/admin/ui";

const Metric = ({ icon: Ico, label, value, sub, def, testid }) => (
  <div className="flex items-start gap-4 rounded-lg bg-paleblue p-5" data-testid={testid}>
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-navy"><Ico size={22} aria-hidden="true" /></span>
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-sm text-slate-600">{label}<InfoTip label={label} text={def} /></p>
      <p className="font-serif text-2xl font-bold text-navy" data-testid={`${testid}-value`}>{value}</p>
      {sub && <p className="text-xs text-slate-600">{sub}</p>}
    </div>
  </div>
);

const Checklist = ({ event, payment, counts }) => {
  const items = [["Event details confirmed (date & venue)", event.date_confirmed && event.venue_confirmed], ["Registration categories", true],
    ["Live payment provider", payment.configured && !payment.is_test], ["Email provider", false], ["Virtual event setup", false],
    ["Sponsorship packages", counts > 0], ["Website content published", true], ["Go live", false]];
  return (
    <div className="admin-card" data-testid="event-setup-progress">
      <h2 className="text-lg text-navy">Event Setup Progress</h2>
      <ul className="mt-3 space-y-2 text-sm">
        {items.map(([l, ok]) => <li key={l} className="flex items-center gap-2">{ok ? <CheckCircle2 size={17} className="text-green-700" aria-hidden="true" /> : <Circle size={17} className="text-slate-400" aria-hidden="true" />}{l}<span className="sr-only">{ok ? "done" : "pending"}</span></li>)}
      </ul>
    </div>
  );
};

export default function Dashboard() {
  const { payment } = useSite();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p aria-busy="true">Loading…</p>;
  const m = d.metrics, ev = d.event, df = d.definitions;
  const pct = (n) => (m.total_registrations ? `${Math.round((n / m.total_registrations) * 100)}% of total` : "0% of total");
  const cols = [
    { key: "name", label: "Name", render: (r) => <span className="font-semibold text-navy">{r.first_name} {r.last_name} <TestBadge show={r.is_test} /></span> },
    { key: "category_name", label: "Category" }, { key: "mode", label: "Attendance", render: (r) => MODE_LABELS[r.attendance_mode] },
    { key: "payment_status", label: "Payment", render: (r) => <StatusPill value={r.payment_status} /> }, { key: "date", label: "Date", render: (r) => shortDate(r.created_at) },
  ];
  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" subtitle={ev.name} />
      <p className="-mt-4 mb-6 flex flex-wrap gap-5 text-sm text-slate-600">
        <span className="flex items-center gap-1.5"><CalendarDays size={16} aria-hidden="true" />{formatEventDate(ev)}{!ev.date_confirmed && <b className="text-burgundy"> · {ev.date_note}</b>}</span>
        <span className="flex items-center gap-1.5"><MapPin size={16} aria-hidden="true" />{venueLine(ev)}</span>
      </p>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Metric icon={Users} label="Total Registrations" value={m.total_registrations} def={df.total_registrations} testid="metric-total-registrations" />
        <Metric icon={Coins} label={`Verified Revenue (${m.currency})`} value={money(m.verified_revenue, m.currency)} sub={`${m.verified_payments} completed payments · Refunds ${money(m.refunds, m.currency)}`} def={df.verified_revenue} testid="metric-verified-revenue" />
        <Metric icon={Monitor} label="Virtual Attendees" value={m.virtual} sub={pct(m.virtual)} def={df.attendance_split} testid="metric-virtual" />
        <Metric icon={UserCheck} label="In-Person Attendees" value={m.in_person} sub={pct(m.in_person)} def={df.attendance_split} testid="metric-in-person" />
        <Metric icon={Handshake} label="Confirmed Sponsors" value={m.confirmed_sponsors} sub={`of ${m.sponsor_target} target`} def={df.confirmed_sponsors} testid="metric-confirmed-sponsors" />
        <Metric icon={Target} label="Attendance Target" value={`${m.attendance_progress}%`} sub={`${m.confirmed_registrations} confirmed of ${m.attendance_target}`} def={df.attendance_target} testid="metric-attendance-target" />
        <Metric icon={Gift} label="Complimentary (excluded from revenue)" value={m.complimentary_total ?? 0} def={df.complimentary} testid="metric-complimentary"
          sub={(d.complimentary_by_category || []).map((c) => `${c.category}: ${c.count}${c.sponsor_linked ? ` (${c.sponsor_linked} sponsor)` : ""}`).join(" · ") || "None yet"} />
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <div className="mb-3 flex items-center justify-between"><h2 className="text-lg text-navy">Recent Registrations</h2><Link to="/admin/registrations" className="text-sm font-semibold text-burgundy" data-testid="dashboard-view-all-link">View All →</Link></div>
          <DataTable columns={cols} rows={d.recent} testid="dashboard-recent-table" empty="No registrations yet." />
        </div>
        <Checklist event={ev} payment={payment} counts={1} />
      </div>
    </div>
  );
}
